import * as React from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toSlugWithFallback } from '@pageboss/shared';
import { api } from '@/lib/api';
import { UpgradeGate, nextPaidPlan, hasNextPaidPlan, type UpgradePlanKey } from '@/components/upgrade-gate';
import type { PageDetailResponse } from '@/lib/types';
import { cn } from '@/lib/utils';

interface CreatePageModalProps {
  open: boolean;
  siteId: string;
  onClose: () => void;
  onCreated: (pageId: string) => void;
}

// Slug preview uses the SHARED toSlug so what the user sees here is exactly
// what the server will store (a local copy silently drifted from it before —
// non-ASCII titles previewed differently than they were saved).

interface ApiError {
  error?: string;
  slug?: string;
  limit?: number;
  tier?: string;
}

export function CreatePageModal({ open, siteId, onClose, onCreated }: CreatePageModalProps) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [title, setTitle] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [gate, setGate] = React.useState<{ capability: string; planKey: UpgradePlanKey } | null>(null);
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const titleInputRef = React.useRef<HTMLInputElement | null>(null);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);

  // Reset state each time the modal opens.
  React.useEffect(() => {
    if (open) {
      setTitle('');
      setSlug('');
      setSlugTouched(false);
      setError(null);
      setGate(null);
      restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
      requestAnimationFrame(() => titleInputRef.current?.focus());
    } else {
      restoreFocusRef.current?.focus?.();
    }
  }, [open]);

  // ESC closes.
  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  // Auto-derive slug from title until the user touches the slug field. Uses the
  // SAME helper the server uses when no slug is sent (toSlugWithFallback), so a
  // short/unmappable title previews the value that will actually be stored
  // ("A" → page-a) instead of a different one. The trim guard keeps the field
  // showing its "auto-from-title" placeholder before anything is typed.
  const previewSlug = slugTouched ? slug : title.trim() ? toSlugWithFallback(title, 'page') : '';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    setGate(null);
    const payload: { title: string; slug?: string } = { title: title.trim() };
    if (slugTouched && slug.trim()) payload.slug = slug.trim();
    try {
      const res = await api.post<PageDetailResponse>(
        locationApi(businessId, siteId, 'pages'),
        payload,
      );
      onCreated(res.page.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create the page.';
      // Try to extract a friendlier message from the API error JSON.
      let humanized = message;
      let gated = false;
      try {
        const parsed = JSON.parse(message) as ApiError;
        if (parsed.error === 'slug_taken') {
          humanized = `That slug is already used on this site. Try a different one.`;
        } else if (parsed.error === 'slug_reserved') {
          humanized = `"${parsed.slug ?? ''}" is reserved. Pick a different slug.`;
        } else if (parsed.error === 'page_limit_reached') {
          const tier = parsed.tier ?? 'free';
          if (hasNextPaidPlan(tier)) {
            gated = true;
            setGate({
              capability: `Your plan only allows ${parsed.limit ?? 1} page${(parsed.limit ?? 1) === 1 ? '' : 's'} — you've reached that limit.`,
              planKey: nextPaidPlan(tier),
            });
          } else {
            humanized = `The Growth plan only allows ${parsed.limit ?? 1} pages, and you've reached that limit.`;
          }
        } else if (parsed.error === 'invalid_input') {
          humanized = 'Check the title and slug — something is off.';
        }
      } catch {
        // not JSON, keep raw message
      }
      if (!gated) setError(humanized);
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-page-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-8 sm:py-16"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        disabled={submitting}
        className="fixed inset-0 z-0 bg-text-primary/40 backdrop-blur-sm"
      />

      <div
        ref={cardRef}
        className={cn(
          'relative z-10 w-full max-w-md rounded-2xl bg-card shadow-lg',
          'animate-[panel-enter_180ms_ease-out]',
        )}
      >
        <div className="flex items-start justify-between border-b border-border-subtle px-6 py-4">
          <h3
            id="create-page-title"
            className="text-lg font-semibold tracking-tight text-foreground"
          >
            Add a new page
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={cn(
              'rounded-md p-1.5 text-text-secondary',
              'transition-colors hover:bg-surface-sunken hover:text-foreground',
              'focus-visible:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:opacity-50',
            )}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-page-title">Page title</Label>
            <Input
              ref={titleInputRef}
              id="new-page-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. About us"
              maxLength={160}
              disabled={submitting}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-page-slug">URL slug</Label>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-text-tertiary">/</span>
              <Input
                id="new-page-slug"
                value={slugTouched ? slug : previewSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="auto-from-title"
                disabled={submitting}
                className="font-mono text-sm"
              />
            </div>
            <p className="text-xs text-text-tertiary">
              Lowercase letters, numbers, and dashes. Auto-generated from the title.
            </p>
          </div>

          {gate ? (
            <UpgradeGate capability={gate.capability} planKey={gate.planKey} from={window.location.pathname} />
          ) : error ? (
            <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
              {error}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !title.trim()}>
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create page'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
